/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useState, useContext } from "react";
import * as Sentry from "@sentry/react";
import * as braze from "@braze/web-sdk";
import Modal from "react-modal";
import axios from "axios";
import { isValidEmail, useQueryParamsAsJson } from "@tomocredit/common-fe/utils";
import { ANALYTICS_EVENTS, LEAD_SOURCES } from "@tomocredit/common-fe/consts";
import BoostRocket from "images/boost/boost-rocket-email.svg";
import { BreakpointContext } from "components/common";
import arrowRightIcon from "images/arrow-right.svg";

interface EmailInputPopupProps {
  isVisible: boolean;
  onClose: () => void;
  onSubmit: (email: string) => void;
  name: string;
  price: number;
}

const modalStyle = {
  overlay: {
    zIndex: 50,
    backgroundColor: "#00000080", // black with a bit of transparency
  },
  content: {
    zIndex: 100,
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    border: 0,
    padding: "0 0 0 0",
    outline: "none",
    transform: "translate(-50%, -50%)",
    background: "transparent",
    overflow: "hidden",
    // minWidth: "358px",
  },
};

const EmailInputPopup: React.FC<EmailInputPopupProps> = ({
  isVisible,
  onClose,
  onSubmit,
  name,
  price,
}) => {
  const { isBelow } = useContext(BreakpointContext);
  const belowMd = isBelow("md");

  const [formEmail, setFormEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [productEnrolled, setProductEnrolled] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const {
    capturedUrlParams,
  } = useQueryParamsAsJson(["email"]);

  const handleEmailInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailInput = (e.target?.value?.trim() || "").toLowerCase();
    setFormEmail(emailInput);
    setProductEnrolled(false);
    if (emailInput.length && !isValidEmail(emailInput)) {
      setEmailError("Please enter a valid email");
    } else {
      setEmailError("");
    }
  };

  const handleSubmit = async () => {
    if (loading) {
      return;
    }

    if (!formEmail || !isValidEmail(formEmail)) {
      setEmailError("Please enter a valid email");
      return;
    }
    setEmailError("");

    setLoading(true);
    let signUpBoostRes;
    try {
      const payload = { email: formEmail, source: LEAD_SOURCES.BOOST_PRICING, capturedUrlParams };
      signUpBoostRes = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/boost/signup`,
        payload,
      );
    } catch (err) {
      setEmailError("Could not communicate with server. Please try again later");
      return;
    } finally {
      setLoading(false);
    }

    const { boostEnrolled, userExists, guid } = signUpBoostRes?.data ?? {};

    if (guid) {
      braze.changeUser(guid);
      braze.getUser()?.setEmail(formEmail);
    }

    if (boostEnrolled) {
      setEmailError("You are already enrolled.");
      setProductEnrolled(true);
      return;
    }
    if (!userExists) {
      window?.gtag?.("event", "boost_pricing_new_signup", {
        email: formEmail,
      });
      window?.gtag?.("event", ANALYTICS_EVENTS.NEW_LEAD, {
        email: formEmail,
        source: LEAD_SOURCES.BOOST_PRICING,
      });
      window.uetq?.push('event', 'lead',
        {
          event_label:"lead",
          event_category:"lead",
          contact: formEmail,
        },
      );
      if (typeof window.gtag !== "function") {
        Sentry.captureException(new Error(`New signup detected in ${LEAD_SOURCES.BOOST_PRICING} but gtag is not loaded`));
      }
    }
    onSubmit(formEmail);
  };

  return (
    <Modal
      isOpen={isVisible}
      onRequestClose={onClose}
      contentLabel="Boost Email Modal"
      style={modalStyle}
      ariaHideApp={false}
    >
      <div
        className="mx-[10px] w-full h-full flex flex-col justify-center items-center text-black bg-white overflow-hidden"
        style={{ borderRadius: 8, width: belowMd ? 358 : 564 }}
        data-testid="boost-email-modal"
      >
        <div className="w-full relative" style={{ background: "linear-gradient(143.99deg, #E3DBFD 19.8%, #C7B6FB 73.46%)" }}>
          <div
            style={{
              width: 20,
              height: 20,
              fontSize: 28,
              paddingTop: 2,
            }}
            className="mt-4 mr-4 flex justify-center items-center absolute text-center text-black font-medium bg-gray-400 rounded-full top-0 right-0 cursor-pointer leading-none"
            onClick={onClose}
          >
            &times;
          </div>
          <div className="flex justify-center py-5">
            <img alt="" src={BoostRocket} className="" style={{ width: 120, height: 120 }} />
          </div>
        </div>
        <div className="w-full flex flex-col p-12 leading-none" style={{ letterSpacing: -0.25 }}>
          <div className=" text-2xl font-bold">
            Get ready to boost your credit!
          </div>
          <div className="mt-6 text-success-500 text-2xl font-bold">
            {name} Plan - ${price.toFixed(2)}/mo
          </div>
          <div className="mt-4 flex flex-col">
            <span className="font-medium text-xs leading-normal" style={{ letterSpacing: 0.1 }}>
              Payment options include credit card, Link, Cash App, Apple Pay,
              Google Pay.
            </span>
            <div className="flex mt-1">
              <input
                style={{
                  height: 40,
                  backgroundColor: "#FFFFFF",
                  borderTopLeftRadius: 4,
                  borderBottomLeftRadius: 4,
                  paddingLeft: 15,
                  color: "#5F5F5F",
                }}
                className="w-full border border-neutral-500 focus:outline-none"
                placeholder="Enter your email"
                onChange={handleEmailInput}
                onSubmit={handleSubmit}
                onKeyUp={(e) => {
                  if (e.key === "Enter") {
                    handleSubmit();
                  }
                }}
                data-testid="boost-email-modal-input"
              />
              <button
                type="button"
                data-testid="boost-email-modal-submit"
                style={{
                  padding: "8px 22px 8px 22px",
                  backgroundColor: "#7249F5",
                  borderTopRightRadius: 4,
                  borderBottomRightRadius: 4,
                  height: 40,
                  minWidth: 67,
                }}
                onClick={handleSubmit}
              >
                <img src={arrowRightIcon} alt="Go to checkout" />
              </button>
            </div>
            <div style={{ color: "#FFA9A9" }} className="mt-1 flex">
              <p>{emailError}</p>
              {productEnrolled && (
                <div className="flex whitespace-pre flex-wrap">
                  <span> Click </span>
                  <a
                    href={`${process.env.REACT_APP_DOMAIN}/dashboard/login?redirect_uri=/boost&fromBoost=true`}
                    onClick={(e) => e.currentTarget.focus()}
                    className="same-host-redirect"
                  >
                    <p
                      className="hover:underline"
                    >
                    here
                    </p>
                  </a>
                  <span> to log in.</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EmailInputPopup;
